<script setup lang="ts">
// The environment's options after create (PUT /environments/{name}/options): mitmproxy on or off. fluentbit and
// mailpit always run and are shown for completeness. The API decides; this only offers what the caller's role allows.
import { computed, ref, watch } from 'vue';
import { must } from '../api/client';
import type { Environment } from '../api/types';
import { useContext } from '../context';
import ProblemAlert from './ProblemAlert.vue';

const props = defineProps<{ env: Environment }>();
const emit = defineEmits<{ changed: [env: Environment] }>();
const { api } = useContext();

const canChange = computed(() => !!props.env.yourRole && props.env.yourRole !== 'viewer' && !props.env.deleting && !props.env.tornDown);
const mitmproxy = ref(props.env.spec.mitmproxy !== false);
watch(() => props.env.spec.mitmproxy, (v) => { mitmproxy.value = v !== false; });
const dirty = computed(() => mitmproxy.value !== (props.env.spec.mitmproxy !== false));
const busy = ref(false);
const error = ref<unknown>();
const message = ref('');

async function save() {
  busy.value = true;
  error.value = undefined;
  message.value = '';
  try {
    const e = must(await api.PUT('/environments/{name}/options', { params: { path: { name: props.env.name } }, body: { mitmproxy: mitmproxy.value } }));
    message.value = `mitmproxy ${mitmproxy.value ? 'turned on' : 'turned off'}; the operator re-applies the chart.`;
    emit('changed', e);
  } catch (e) {
    error.value = e;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <section aria-labelledby="options-h" style="margin-top: 1.25rem" data-testid="options">
    <h3 id="options-h" style="margin: 0 0 0.5rem">Options</h3>
    <ProblemAlert v-if="error" :error="error" />
    <form novalidate style="display: flex; flex-direction: column; gap: 0.5rem" @submit.prevent="save">
      <label class="inline">
        <input v-model="mitmproxy" type="checkbox" :disabled="!canChange || busy" data-testid="opt-mitmproxy" />
        <span><strong>mitmproxy</strong> <span class="muted">captures the shop's outbound HTTP calls; on unless turned off</span></span>
      </label>
      <label class="inline"><input type="checkbox" checked disabled /><span><strong>fluentbit</strong> <span class="muted">always on: ships the logs the Logs tab reads</span></span></label>
      <label class="inline"><input type="checkbox" checked disabled /><span><strong>mailpit</strong> <span class="muted">always on: catches every mail on mail:25 for the Mail tab</span></span></label>
      <div v-if="canChange" class="inline">
        <button type="submit" :disabled="!dirty || busy" data-testid="opt-save">Save options</button>
        <span v-if="message" class="muted" role="status">{{ message }}</span>
      </div>
      <p v-else class="muted" style="margin: 0; font-size: 13px">An operator of this environment can change the options.</p>
    </form>
  </section>
</template>
